import { EventEmitter } from 'events';
import { Box3, Mesh, Object3D, Vector3 } from 'three';

import { Instance } from '../objects/index.js';

import { Octree } from './threejs-octree/index.js';

export type ThreeOctreeEvents = {
  ready: [];
};

export class ThreeOctree extends EventEmitter<ThreeOctreeEvents> {
  //@ts-ignore
  octree: Octree<Object3D> = new Octree<Object3D>(10, 4);
  objects: Set<Object3D> = new Set();
  bounds: Box3 = new Box3();
  isInitialised: boolean = false;

  setBounds(bounds: Box3) {
    const objects = [...this.objects].filter((o) => (o as Mesh).geometry);
    this.bounds.copy(bounds);
    this.octree.initialize(bounds.min, bounds.max, objects as Mesh[]);
    this.isInitialised = true;
    this.emit('ready');
  }

  addObject(object: Object3D) {
    if (!(object as Mesh).geometry) {
      if ((object as Instance).isInstance) {
        // wait for the instance to be mounted and then get its geometry
        const instance = object as Instance;
        const listener = () => {
          if (instance.geometry) {
            this.addObject(instance);
            instance.removeEventListener('mount', listener);
          }
        };
        instance.addEventListener('mount', listener);
      }
      return;
    }
    this.objects.add(object);
    this.octree.addMesh(object as Mesh);
  }

  removeObject(object: Object3D) {
    this.objects.delete(object);
    this.octree.removeMesh(object as Mesh);
  }

  updateObject(object: Object3D) {
    if (!(object as Mesh).geometry) {
      return;
    }
    this.octree.updateMesh(object as Mesh);
  }

  updateObjects(objects: Object3D[]) {
    if (!this.isInitialised) {
      this.objects.clear();
      objects.forEach((o) => this.objects.add(o));
      return;
    }
    const newObjects = new Set(objects);
    const objectsToRemove: Object3D[] = [];
    const objectsToUpdate: Object3D[] = [];

    this.objects.forEach((object) => {
      if (newObjects.has(object)) {
        objectsToUpdate.push(object);
        newObjects.delete(object);
      } else {
        objectsToRemove.push(object);
      }
    });

    newObjects.forEach((o) => {
      this.addObject(o);
    });

    for (let i = 0; i < objectsToRemove.length; i++) {
      this.removeObject(objectsToRemove[i]);
    }

    for (let i = 0; i < objectsToUpdate.length; i++) {
      this.updateObject(objectsToUpdate[i]);
    }
  }

  retrieve(sphereCenter: Vector3, sphereRadius: number): Object3D[] {
    return this.octree.intersects(sphereCenter, sphereRadius).array as Object3D[];
  }
}
